import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import {
    IoArrowBack,
    IoCheckmarkCircle,
    IoCheckmarkCircleOutline,
    IoCloseCircle,
    IoCloseCircleOutline,
    IoTimeOutline,
    IoTrashOutline,
    IoShieldCheckmarkOutline,
    IoHomeOutline,
    IoStorefrontOutline,
    IoWalletOutline,
    IoSettingsOutline,
    IoHeartOutline
} from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './MuroScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import BotonPanico from '../components/Safety/BotonPanico';

// Repetir el componente de Badge para consistencia
const CategoryBadge = ({ type }) => {
    let className = 'badge-aviso';
    if (type === 'venta') className = 'badge-venta';
    if (type === 'servicio') className = 'badge-servicio';
    if (type === 'evento') className = 'badge-evento';
    return <span className={`post-badge ${className}`}>{type}</span>;
};

const AdminMuroScreen = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados para Modales
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        if (!userData || !userData.condominioId) return;

        setLoading(true);
        const postsRef = collection(db, 'muro_posts');
        const q = query(
            postsRef,
            where('condominioId', '==', userData.condominioId),
            where('estatus', '==', 'pendiente'),
            orderBy('fechaCreacion', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const postsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPosts(postsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching admin posts:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userData]);

    const handleApproveClick = (postId) => {
        setSelectedPostId(postId);
        setShowConfirmModal(true);
    };

    const handleRejectClick = (postId) => {
        setSelectedPostId(postId);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    const confirmApprove = async () => {
        if (!selectedPostId) return;
        try {
            await updateDoc(doc(db, 'muro_posts', selectedPostId), {
                estatus: 'aprobado'
            });
            setShowConfirmModal(false);
            setSelectedPostId(null);
        } catch (error) {
            console.error("Error confirming post:", error);
            alert("Error al aprobar.");
        }
    };

    const confirmReject = async () => {
        if (!selectedPostId || !rejectionReason.trim()) {
            alert("El motivo de rechazo es obligatorio.");
            return;
        }

        try {
            await updateDoc(doc(db, 'muro_posts', selectedPostId), {
                estatus: 'rechazado',
                razonRechazo: rejectionReason
            });
            setShowRejectModal(false);
            setSelectedPostId(null);
        } catch (error) {
            console.error("Error rejecting post:", error);
            alert("Error al rechazar.");
        }
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/admin/muro" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    {/* ── STITCH HERO BANNER (flows with content) ── */}
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Moderación</h1>
                                <p>Revisa y gestiona las publicaciones de la comunidad para asegurar un ambiente seguro.</p>
                            </div>
                            <IoShieldCheckmarkOutline className="muro-hero-icon-decor" />
                        </div>
                    </div>

                    <div className="muro-content-container" style={{ paddingBottom: '120px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando pendientes...</div>
                ) : posts.length === 0 ? (
                    <div className="empty-state" style={{ marginTop: 20 }}>
                        <IoCheckmarkCircleOutline size={48} color="#10b981" />
                        <h3 style={{ margin: '15px 0 5px' }}>¡Todo al día!</h3>
                        <p style={{ color: '#6b7280' }}>No hay publicaciones pendientes de revisión.</p>
                    </div>
                ) : (
                    <div className="muro-feed-grid">
                        {posts.map(post => (
                            <div key={post.id} className="post-card-stitch" style={{ border: '2px dashed #f59e0b' }}>
                                <div className="post-header-stitch">
                                    <div className="post-author-row">
                                        <div className="post-avatar-stitch">
                                            {(post.usuarioNombre || "U").charAt(0).toUpperCase()}
                                        </div>
                                        <div className="post-meta-stitch">
                                            <span className="post-name-stitch">{post.usuarioNombre}</span>
                                            <span className="post-date-stitch">
                                                {post.fechaCreacion?.toDate ? moment(post.fechaCreacion.toDate()).fromNow() : "Pendiente"}
                                                {post.usuarioUnidad && ` • ${post.usuarioUnidad}`}
                                            </span>
                                        </div>
                                    </div>
                                    <CategoryBadge type={post.tipo} />
                                </div>

                                <div className="post-content-stitch">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                        <div className="post-title-stitch">{post.titulo}</div>
                                        {post.tipo === 'venta' && post.precio && (
                                            <div className="post-price-badge-stitch">${post.precio}</div>
                                        )}
                                    </div>
                                    <div className="post-body-stitch" style={{ display: 'block', WebkitLineClamp: 'unset' }}>
                                        {post.contenido}
                                    </div>
                                </div>

                                {post.imagenUrl && (
                                    <div className="post-image-container-stitch">
                                        <img 
                                            src={post.imagenUrl} 
                                            alt="Post" 
                                            className="post-image-stitch" 
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}

                                <div className="post-footer-stitch" style={{ flexDirection: 'column', gap: '12px', alignItems: 'stretch', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                    {(post.likesCount || 0) > 0 && (
                                        <div className="post-interactions-stitch" style={{ marginBottom: '8px' }}>
                                            <IoHeartOutline size={20} className="interaction-icon" style={{ color: '#ef4444' }} />
                                            <span className="interaction-counter">{post.likesCount}</span>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            onClick={() => handleApproveClick(post.id)}
                                            className="contact-btn-stitch"
                                            style={{ flex: 1, backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                        >
                                            <IoCheckmarkCircleOutline size={20} /> Aprobar
                                        </button>
                                        <button
                                            onClick={() => handleRejectClick(post.id)}
                                            className="contact-btn-stitch"
                                            style={{ flex: 1, backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                        >
                                            <IoCloseCircleOutline size={20} /> Rechazar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        <KondoBottomNav activeRoute="/admin/muro" />
      </main>

            {/* ── CUSTOM MODALS ── */}
            {showConfirmModal && (
                <div className="premium-modal-overlay">
                    <div className="premium-modal" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ marginBottom: '20px', color: '#10b981' }}>
                            <IoCheckmarkCircleOutline size={64} />
                        </div>
                        <h2 style={{ marginBottom: '12px' }}>¿Aprobar Publicación?</h2>
                        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                            Esta publicación será visible para todos los residentes del condominio inmediatamente.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button className="muro-hero-action-btn" style={{ flex: 1, justifyContent: 'center', background: '#f3f4f6', color: '#4b5563' }} onClick={() => setShowConfirmModal(false)}>
                                Cancelar
                            </button>
                            <button className="muro-hero-action-btn" style={{ flex: 1, justifyContent: 'center', background: '#10b981', color: 'white' }} onClick={confirmApprove}>
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRejectModal && (
                <div className="premium-modal-overlay">
                    <div className="premium-modal" style={{ maxWidth: '450px' }}>
                        <h2 style={{ marginBottom: '8px' }}>Rechazar Publicación</h2>
                        <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.9rem' }}>
                            Indica el motivo por el cual la publicación no cumple con las normas de la comunidad. El residente podrá ver este motivo.
                        </p>
                        
                        <textarea
                            className="premium-input-standard"
                            placeholder="Ej: Contenido inapropiado, falta de información..."
                            style={{ width: '100%', minHeight: '100px', marginBottom: '20px', padding: '12px' }}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button className="muro-hero-action-btn" style={{ flex: 1, justifyContent: 'center', background: '#f3f4f6', color: '#4b5563' }} onClick={() => setShowRejectModal(false)}>
                                Cancelar
                            </button>
                            <button 
                                className="muro-hero-action-btn" 
                                style={{ flex: 1, justifyContent: 'center', background: '#ef4444', color: 'white' }} 
                                onClick={confirmReject}
                                disabled={!rejectionReason.trim()}
                            >
                                Rechazar Post
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMuroScreen;
